// The Trending card's COPY — headline, detail body, and the external platform link.
//
// Presentation, deliberately: the server sends facts (platform, metric, pct, the series, a post's caption and
// permalink) and the wording lives here, exactly as the spec does it (its worker stored numbers;
// `lib/trending.ts` built every string client-side). Keeping copy off the read model is also what lets the
// same DTO serve the roster lens without re-deciding tone per surface.
//
// ONE authority for all of it, because the spec's own rule is "desktop, roster, and mobile say exactly the
// same words because they all call the same builders — never fork the copy".

import { platformLabel, parseDomainDate } from "./platforms.js";

/** Metric display names. A metric with no entry degrades to its de-underscored key rather than a blank. */
const METRIC_LABELS: Record<string, string> = {
  followers_total: "followers",
  monthly_listeners_current: "monthly listeners",
  streams_total: "streams",
  streams_28d: "streams (28d)",
  playlist_reach_total: "playlist reach",
  playlist_reach_current: "playlist reach",
  views: "views",
};

const trendingMetricLabel = (metric: string | null | undefined): string =>
  metric ? (METRIC_LABELS[metric] ?? metric.replace(/_/g, " ")) : "";

/** "+23%". Always signed up — only positive spikes are ever detected. */
export const trendingPctLabel = (pct: number | null | undefined): string =>
  `+${Math.round(Number(pct) || 0)}%`;

/**
 * The detection day for display: "Today" / "Yesterday" for the fresh cases, then "Aug 19".
 * Parsed through the shared {@link parseDomainDate} — the zoneless-domain-date rule is a function to call,
 * not a comment to restate.
 */
export function trendingWhenLabel(detectedOn: string | null | undefined, now: Date = new Date()): string {
  if (!detectedOn) return "";
  const d = parseDomainDate(detectedOn);
  if (Number.isNaN(d.getTime())) return "";
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(d)) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** A series day as an axis caption — "Jul 5", the same short form the pill's when-label falls back to. */
export const trendingDayLabel = (day: string | null | undefined): string => {
  if (!day) return "";
  const d = parseDomainDate(day);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export interface TrendingCopyInput {
  platform?: string | null;
  metric?: string | null;
  pct?: number | null;
  /** The platform's analytics console — or, for a POST item, the post's own permalink. Server-resolved. */
  url?: string | null;
  /** "platform" (artist-grain metric), "post" (one post's views) or "track" (one song's streams). Branch on
   *  THIS, never on `subject`: that string is a dedupe key, and rendering it raw is how a card came to read
   *  "Platform:Tiktok:Followers_total". */
  kind?: string | null;
  /** What this item is CALLED — a post's caption, a track's name. One slot per kind, because it answers one
   *  question; a second nullable string beside it would be the same field twice. */
  caption?: string | null;
}

/** "video" on the platforms whose unit of publishing is one, "post" on the feeds. The spec keeps this as a
 *  noun rather than always saying "post": a TikTok creator does not call their clip a post. */
const postNoun = (platform: string | null | undefined): string =>
  platform === "tiktok" || platform === "youtube" ? "video" : "post";

const truncate = (s: string, max: number): string => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
};

const isPost = (item: TrendingCopyInput) => item.kind === "post";
const isTrack = (item: TrendingCopyInput) => item.kind === "track";

/** A song's name for a headline — the server drops a nameless track before it ever reaches a client, so the
 *  fallback is for a malformed row rather than an expected state. */
const trackName = (item: TrendingCopyInput) => truncate(item.caption ?? "", 48) || "Your track";

/**
 * The pill headline.
 *
 * A post says WHAT HAPPENED and lets the thumbnail say which clip — the spec's wording, restored 2026-09-06
 * (ART-82). It shipped instead as "«caption» jumped +40%" on the reasoning that a caption is the only
 * actionable part; the spec's answer to that is the THUMB, which the pill was missing at the time, and the
 * DETAIL body, which names the caption in full. With both in place the caption no longer has to fight the
 * headline for the one line it gets.
 */
export function trendingHeadline(item: TrendingCopyInput): string {
  // A track LEADS WITH THE SONG, where a post leads with the event. The asymmetry is the spec's and it is
  // right: one clip among forty is identified by its thumbnail, but a song is identified by its name — and
  // "Spotify streams jumped" is the artist-level row this one exists to be more specific than.
  if (isTrack(item)) {
    return `“${trackName(item)}” had a major jump in streams on ${platformLabel(item.platform)}`;
  }
  if (isPost(item)) {
    return `This ${platformLabel(item.platform)} ${postNoun(item.platform)} saw a big spike in views`;
  }
  return `${platformLabel(item.platform)} ${trendingMetricLabel(item.metric)} jumped ${trendingPctLabel(item.pct)}`;
}

export interface TrendingDetail {
  heading: string;
  body: string;
  /** External platform link, or "" when we have no honest destination for that platform. */
  linkUrl: string;
  linkLabel: string;
}

/** The LABEL for each platform's analytics console. The URL itself rides the DTO (`item.url`) — a host is not
 *  something app source hardcodes here, and the server is the one place that knows whether it can name a real
 *  destination at all. Copy stays here; only platforms the server can link get a label.
 *
 *  Wider than the spec, deliberately and already shipped: its worker only ever sent a URL for Spotify, so its
 *  non-Spotify branch read "Open YouTube". Ours resolves the real console, so the button names it. */
const CONSOLE_LABELS: Record<string, string> = {
  spotify: "Spotify for Artists",
  youtube: "YouTube Studio",
  tiktok: "TikTok Studio",
};

/**
 * The detail-view recommendation copy — prescriptive by KIND, per the spec:
 * a post nudges toward more of the same content, Spotify sends you to Spotify for Artists, and every other
 * platform's spike nudges the Content tab. There is deliberately no extra CTA beyond the one link.
 */
export function trendingDetail(item: TrendingCopyInput): TrendingDetail {
  const platform = platformLabel(item.platform);
  const pct = trendingPctLabel(item.pct);

  if (isTrack(item)) {
    // The song links to ITSELF (its Songstats page), but the recommendation still points at Spotify for
    // Artists: "where did the streams come from" is a question only the console answers, and the track lane
    // is Spotify-only by construction.
    const name = trackName(item);
    return {
      heading: `“${name}” is trending`,
      body:
        `“${name}” had a major spike in streaming (${pct} in a day)! We recommend you keep a close eye on ` +
        `it and create some content using this song. To see where the streams came from, visit Spotify for ` +
        `Artists.`,
      linkUrl: item.url ?? "",
      linkLabel: item.url ? "Open the song" : "",
    };
  }

  if (isPost(item)) {
    // A post links to ITSELF — the server sends the permalink rather than the platform console, because
    // sending someone to TikTok Studio to look at one clip is a worse answer than opening the clip.
    const noun = postNoun(item.platform);
    const postUrl = item.url ?? "";
    const captioned = item.caption ? ` “${truncate(item.caption, 60)}”` : "";
    return {
      heading: `Your ${platform} ${noun} is trending`,
      body:
        `Your ${platform} ${noun}${captioned} had a big spike in views (${pct} in a day)! Keep a close ` +
        `eye on it and create similar content using the same audio & style. Use the Content tab to help ` +
        `with ideas.`,
      linkUrl: postUrl,
      linkLabel: postUrl ? `Open on ${platform}` : "",
    };
  }

  const metric = trendingMetricLabel(item.metric);
  const consoleUrl = item.url ?? "";
  const consoleLabel = CONSOLE_LABELS[String(item.platform ?? "")];
  const linked = Boolean(consoleUrl && consoleLabel);
  const isSpotify = item.platform === "spotify";
  const streamsy = /stream|listener/.test(String(item.metric ?? ""));

  // Spotify is where the "where did it come from" question has an answer worth walking to; everywhere else
  // the useful next move is more content, which is the app's own Content tab.
  const body = isSpotify
    ? `Your Spotify ${metric} had a major spike (${pct} in a day)! We recommend you keep a close eye on it` +
      `${streamsy ? " and create some content while the momentum is hot" : ""}. To see where the ` +
      `${streamsy ? "streams" : "growth"} came from, visit Spotify for Artists.`
    : `Your ${platform} ${metric} had a big spike (${pct} in a day)! Momentum like this is worth riding, ` +
      `post while the algorithm is favoring you. Use the Content tab to help with ideas.`;

  return {
    heading: `${platform} ${metric} are trending`,
    body,
    linkUrl: linked ? consoleUrl : "",
    linkLabel: linked ? consoleLabel : "",
  };
}
